import { formSessionError } from '@jukun/platform-sdk/core';
import { copyFormSessionData, runFormSessionOperation, sameFormSessionToken, type FormSessionToken } from '@jukun/platform-sdk/shell';
import { validateProcessInitiationViewContext, validateWorkflowPageContext } from '@jukun/platform-sdk/workflow';
import type { PortalApplication } from '../../../model/portalModels';
import type { FormSelectionTarget, FormSelectionTicket, PortalFormSessionManager } from '../../../runtime/formSessionManager';
import { FORM_SESSION_OPEN_BUDGET_MS, FormLeaveCancelledError } from '../../../runtime/formSessionManager';
import { myTasksApi } from '../api/myTasksApi';
import type { PortalTaskDetail, PortalTaskGroupKey } from '../model/myTasks';
import { isBuiltInTenantApplication } from './workItemRuntime';

export type TaskNavigationOptions = Readonly<{ force?: boolean; replace?: boolean; refresh?: boolean }>;
export type TaskNavigate = (path: string, options?: TaskNavigationOptions) => Promise<boolean>;
export type PreparedTaskSelection = Readonly<{
  group: PortalTaskGroupKey; itemId: string; ticket: FormSelectionTicket;
  detail: PortalTaskDetail; token?: FormSessionToken; sourceFailure?: unknown;
  pendingForm?: Promise<PreparedTaskSelection>;
  authorization: 'SESSION' | 'LEAVE';
}>;
type TaskPreparationNavigation = Readonly<{ isCurrent: () => boolean; leaveCurrent: () => Promise<boolean> }>;

export function hasLocalWorkItemSave(detail: PortalTaskDetail) {
  return detail.flowPageContext?.recipientType === 'TODO'
    && detail.summary.category === 'todo'
    && detail.availableActions.some(action => action.actionCode === 'SAVE' && action.operationType === 'SHELL_LOCAL')
    && detail.flowPageContext.allowedActions.some(action => action.actionCode === 'SAVE'
      && action.operationType === 'SHELL_LOCAL' && action.formValidationPolicy === 'SKIP');
}

/** Shared by Shell navigation and the initial direct route; no page is cleared while preparing. */
export class TaskFormSelectionCoordinator {
  private readonly requests = new WeakMap<FormSelectionTicket, Promise<PreparedTaskSelection>>();
  private readonly navigations = new WeakMap<FormSelectionTicket, TaskPreparationNavigation>();
  private readonly failedRequests = new WeakSet<FormSelectionTicket>();
  constructor(private readonly manager: PortalFormSessionManager,
    private readonly applications: () => readonly PortalApplication[],
    private readonly readDetail = myTasksApi.detail) {}

  prepare(group: PortalTaskGroupKey, itemId: string, options: Readonly<{
    force?: boolean; isCurrent: () => boolean; leaveCurrent: () => Promise<boolean>;
  }>): Promise<PreparedTaskSelection> {
    // Request reference is known before detail; an initiated card is never an instance ID.
    const target: FormSelectionTarget = group === 'initiated'
      ? { contextType: 'PROCESS_INITIATION_VIEW', targetRef: itemId, intentKey: group }
      : { contextType: 'WORK_ITEM', subjectPublicId: itemId, intentKey: group };
    let ticket = this.manager.beginSelection(target, { force: options.force });
    // Retry with a new generation: the manager also retains open results for each ticket.
    if (this.failedRequests.has(ticket)) ticket = this.manager.beginSelection(target, { force: true });
    // Repeated clicks share preparation, but the newest route transaction owns its commit.
    this.navigations.set(ticket, options);
    const existing = this.requests.get(ticket);
    if (existing) return existing;
    let showDetail!: (selection: PreparedTaskSelection) => void;
    const detailReady = new Promise<PreparedTaskSelection>(resolve => { showDetail = resolve; });
    const completion = this.readAndOpen(group, itemId, ticket, detail => {
      showDetail(Object.freeze({ group, itemId, ticket, detail, pendingForm: completion, authorization: 'SESSION' }));
    });
    const request = Promise.race([detailReady, completion]);
    this.requests.set(ticket, request);
    // Late failures belong only to their attempt; never cancel the current selection here.
    void completion.then(selection => {
      this.requests.set(ticket, Promise.resolve(selection));
      if (selection.sourceFailure) this.failedRequests.add(ticket);
    },
      () => { this.failedRequests.add(ticket); });
    return request;
  }

  private async readAndOpen(group: PortalTaskGroupKey, itemId: string, ticket: FormSelectionTicket,
    onLeaveCommitted: (detail: PortalTaskDetail) => void): Promise<PreparedTaskSelection> {
    const current = () => this.manager.isCurrentSelection(ticket);
    const assertCurrent = () => { if (!current()) throw formSessionError('STALE'); };
    const result = await runFormSessionOperation(operation =>
      this.readDetail(itemId, group === 'initiated' ? 'initiated' : 'work-item', { signal: operation.signal }),
    { signal: ticket.signal, remainingMs: FORM_SESSION_OPEN_BUDGET_MS }, 'OPEN_TIMEOUT');
    assertCurrent();
    if (result.summary.taskId !== itemId || (group === 'initiated') !== (result.flowPageContext === null)) throw formSessionError('INPUT_INVALID');
    const detail = copyFormSessionData(result);
    if (detail.flowPageContext !== null) {
      const context = validateWorkflowPageContext(detail.flowPageContext);
      if (context.workflowTaskPublicId !== itemId) throw formSessionError('INPUT_INVALID');
    } else if ((detail.summary.initiatedCardPublicId ?? detail.summary.taskId) !== itemId) {
      throw formSessionError('INPUT_INVALID');
    }
    const coreOnly = detail.flowPageContext !== null && isBuiltInTenantApplication(detail)
      || detail.flowPageContext === null && !detail.pageRoute;
    if (coreOnly) {
      const navigation = this.navigations.get(ticket);
      if (!navigation?.isCurrent()) throw formSessionError('STALE');
      if (!await navigation.leaveCurrent()) throw new FormLeaveCancelledError();
      if (!navigation.isCurrent()) throw formSessionError('STALE');
      return Object.freeze({ group, itemId, ticket, detail, authorization: 'LEAVE' });
    }
    const initial = this.manager.getSnapshot().activeSession;
    let removeEligibility: () => void = () => undefined;
    let seenToken: string | undefined;
    // No local SAVE exists in the still-visible source page for the candidate session.
    removeEligibility = this.manager.subscribe(() => {
      const active = this.manager.getSnapshot().activeSession;
      if (!active || active.token.generation !== ticket.generation
          || active.token.sessionId === initial?.token.sessionId || seenToken === active.token.sessionId) return;
      seenToken = active.token.sessionId;
      this.manager.setSaveEligibility(active.token, false);
    });
    try {
      const context = detail.flowPageContext !== null
        ? validateWorkflowPageContext(detail.flowPageContext)
        : validateProcessInitiationViewContext(detail.processInitiationViewContext);
      if (context.contextType === 'PROCESS_INITIATION_VIEW'
          && detail.summary.processInstancePublicId !== context.processInstancePublicId) throw formSessionError('INPUT_INVALID');
      const application = this.applications().find(candidate => candidate.appCode === context.applicationCode && candidate.status === 'ENABLED');
      if (!application || !detail.pageRoute) throw formSessionError('INPUT_INVALID');
      const token = await this.manager.open(ticket, { application, context, pageRoute: detail.pageRoute,
        targetRef: detail.summary.initiatedCardPublicId ?? detail.summary.taskId }, () => onLeaveCommitted(detail));
      assertCurrent();
      return Object.freeze({ group, itemId, ticket, detail, token, authorization: 'SESSION' });
    } catch (sourceFailure) {
      assertCurrent();
      const active = this.manager.getSnapshot().activeSession;
      // Before the old session is relinquished a preparation failure retains its page and input.
      if (initial && active && sameFormSessionToken(initial.token, active.token)) throw sourceFailure;
      if (active && active.token.generation !== ticket.generation) throw sourceFailure;
      return Object.freeze({ group, itemId, ticket, detail, token: active?.token,
        sourceFailure, authorization: 'SESSION' });
    } finally { removeEligibility(); }
  }

  isCurrent(prepared: PreparedTaskSelection) {
    if (prepared.authorization === 'LEAVE') return true; // The navigation transaction owns this commit.
    if (!this.manager.isCurrentSelection(prepared.ticket)) return false;
    if (prepared.pendingForm) return true;
    const active = this.manager.getSnapshot().activeSession;
    return prepared.token ? !!active && sameFormSessionToken(prepared.token, active.token)
      : !active;
  }
}
